'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import contenu from '@/content/contenu';
import {
  CHAMP_PIEGE,
  CODES,
  schemaDeLaVille,
  schemaInscription,
  type DemandeDInscription,
} from '@/content/schemas';
import { VILLES_DU_MAROC } from '@/content/villes';
import Champ from '@/components/ui/Champ';
import { cn } from '@/lib/cn';
import { enHoraireLisible } from '@/lib/dates';
import { envoiReussi } from '@/lib/suiviDAudience';

/**
 * Le parcours d'inscription, ses cinq etapes : la session, les coordonnees, la relecture, le
 * virement, la confirmation.
 *
 * ⚠️ C'EST UN COMPOSANT CLIENT, ET LA DEROGATION QUI L'AUTORISE EST BORNEE PAR DOSSIER.
 * `spec-design.md` v7 § La derogation aux composants client ouvre `/inscription` et les dossiers qui
 * ne servent qu'elle ; `tests/support/gardes/client.js` enumere `components/inscription`, et
 * `AC-ADM-020` refuse la directive ailleurs. La page d'accueil n'importe rien d'ici.
 *
 * POURQUOI IL EST CLIENT, en une phrase qui se verifie : `AC-INS-007` exige que « l'etat d'une etape
 * survive a un retour a l'etape precedente puis a un retour en avant ». Un parcours rendu au serveur
 * perdrait ce qui a ete saisi a chaque aller-retour, ou devrait le reposter a chaque pas.
 *
 * IL NE PORTE AUCUN TEXTE. Tout vient de `contenu.inscription`, recopie de `contenu-site.md` v4
 * § Le parcours d'inscription, et les codes du schema ne s'affichent jamais : ce composant les
 * traduit en messages, et c'est sa seule raison de connaitre `CODES`.
 *
 * LES CINQ ETAPES SONT LA DEPUIS LE 2026-08-15, TACHE 07-07. Les etapes 4 et 5, le virement et la
 * confirmation, sont les pas `RP-02-5` et `RP-02-6`.
 *
 * L'ENVOI SE FAIT A LA FIN DE L'ETAPE 3, PAS DE L'ETAPE 4, ET LA SPEC COMMANDE CET ORDRE :
 * `contenu-site.md` § Etape 4 ecrit son corps au present accompli, « Votre demande est enregistree ».
 * L'ecran du virement affiche donc ce qui est DEJA en base, et le bouton « Terminer » ne fait que
 * mener a l'etape 5. L'autre lecture aurait fait mentir l'etape 4.
 */
const parcours = contenu.inscription;
const etape1 = parcours.etapes.laSession;
const etape2 = parcours.etapes.vosCoordonnees;
const etape3 = parcours.etapes.votreRelecture;
const etape4 = parcours.etapes.leVirement;
const etape5 = parcours.etapes.cEstEnregistre;
/**
 * LE SEUL FRAGMENT AFFICHE PAR CET ECRAN QUI NE VIENNE PAS DE `contenu-site.md`, et il est declare
 * comme tel : `contenu.redactionDev` porte sa section et l'origine de sa tournure, et `AC-DSN-023`
 * compte ces chaines une par une.
 */
const gabaritHoraire = contenu.redactionDev.session.gabaritHoraire.texte;
/** Les quatre intitules qui precedent les valeurs d'une session, a l'etape 1 comme a l'etape 3. */
const intitulesDeSession = etape1.intitules;

/** Ce que l'etape 1 sait d'une session proposee. Les valeurs viennent toutes de la base. */
export type SessionDuParcours = {
  readonly id: string;
  /** La date, deja mise en forme par la page : un composant client ne refait pas ce calcul. */
  readonly jour: string;
  readonly ville: string;
  readonly lieu: string;
  readonly debut: string;
  readonly fin: string;
  readonly placesRestantes: number;
};

/**
 * Les quatre champs de saisie de l'etape 2, dans l'ordre du tableau de la spec.
 *
 * `demiLargeur` DIT QUE LE CHAMP PARTAGE SA LIGNE SUR ORDINATEUR, et il ne vaut que pour l'email et
 * le telephone. Demande de Wahid du 2026-08-20 : « sur desktop mettre Email et Tel du formulaire en
 * 2 colonnes ». Les deux tiennent en une ligne courte ; le prenom et la ville prennent la largeur.
 *
 * `ville` NE S'AFFICHE QUE SI AUCUNE SESSION N'EST RETENUE, ce que `champsAffiches` decide plus bas.
 */
const CHAMPS = [
  { nom: 'prenomNom', rang: 0, type: 'text', autoComplete: 'name', demiLargeur: false },
  { nom: 'email', rang: 1, type: 'email', autoComplete: 'email', demiLargeur: true },
  { nom: 'telephone', rang: 2, type: 'tel', autoComplete: 'tel', demiLargeur: true },
  { nom: 'ville', rang: 3, type: 'text', autoComplete: 'address-level2', demiLargeur: false },
] as const;

type NomDeChamp = (typeof CHAMPS)[number]['nom'] | 'remarque';

/**
 * L'apparence d'un choix de session a l'etape 1 : une carte cliquable.
 *
 * DEMANDE DE WAHID DU 2026-08-20, mot pour mot : « change le layout du choix de la session pour que
 * ce soit une card clickable avec bordure, fond, etat hover, etat actif ». Jusque-la, chaque choix
 * etait un bouton radio suivi de quatre lignes de texte, sans surface : rien ne disait ou finissait
 * un choix et ou commencait le suivant, et la zone cliquable ne se voyait pas.
 *
 * LES QUATRE ETATS, ET LE JETON QUI PORTE CHACUN :
 *   - au repos : surface `fond-eleve`, filet `bordure`, rayon `carte`, ombre `carte` ;
 *   - au survol : filet `bordure-forte` et ombre `carte-survol`, en 150 millisecondes ;
 *   - au clavier : le contour de focus de la page, pose sur la carte et non sur le bouton radio
 *     seul, `focus-within` ;
 *   - actif : filet `action`, double d'un halo de la meme couleur.
 *
 * L'ETAT ACTIF NE REPOSE PAS SUR LA COULEUR SEULE : le bouton radio reste rendu, coche, et c'est lui
 * que porte l'accessibilite. Le filet argile est un renfort visuel, pas la seule marque du choix.
 */
const CARTE_DE_CHOIX = cn(
  'flex cursor-pointer items-start gap-3 rounded-carte border p-4 md:p-5',
  'bg-fond-eleve font-corps text-corps text-texte-courant shadow-carte',
  'transition-[border-color,box-shadow,background-color] duration-150 ease-douce',
  'hover:border-bordure-forte hover:shadow-carte-survol',
  'focus-within:outline-2 focus-within:outline-offset-[3px] focus-within:outline-focus',
);

/**
 * La pastille du nombre de places restantes, en haut a droite de la carte.
 *
 * DEMANDE DE WAHID DU 2026-08-20 : « mettre le nombre de places disponibles dans un label et
 * l'aligner en haut a droite ». Il etait la cinquieme ligne de la colonne de texte, dans la meme
 * graisse et la meme taille que la date et le lieu : c'est pourtant la seule valeur de la carte qui
 * dise s'il est encore possible de s'inscrire.
 *
 * `shrink-0` FACE A UNE COLONNE EN `min-w-0` : sans ces deux classes, la pastille se comprime au
 * lieu de laisser le texte passer a la ligne, et « Il reste 15 places » se casse en trois lignes sur
 * un ecran de telephone.
 *
 * ELLE NE PORTE AUCUN TEXTE : les trois libelles possibles viennent de `contenu.inscription`, par
 * `placesDe`, et « Complet » est l'un d'eux.
 */
const PASTILLE_DE_PLACES = cn(
  'shrink-0 self-start rounded-plein border border-bordure-forte bg-fond-inerte px-3 py-1',
  'font-structure text-etiquette uppercase tracking-etiquette text-texte-doux',
);

/**
 * Ce que la carte du choix retenu ajoute a `CARTE_DE_CHOIX`.
 *
 * `ring-1` ET NON `border-2` : doubler l'epaisseur du filet aurait decale le contenu de la carte
 * d'un pixel au moment du clic, et les cartes voisines de deux. Le halo se dessine hors du flux.
 */
const CARTE_DE_CHOIX_ACTIVE = 'border-action ring-1 ring-action hover:border-action';

/**
 * Le schema de la SEULE etape 2, derive du schema complet.
 *
 * IL SE DERIVE, IL NE SE RECOPIE PAS : `pick` garde la regle du schema partage pour chacun des sept
 * champs. Ecrire ici une seconde liste de contraintes aurait fait exactement ce que
 * `CONVENTIONS.md` § « Un seul schema de validation, partage » nomme en contre-exemple.
 *
 * POURQUOI UNE VALIDATION PAR ETAPE : valider tout a l'etape 2 ferait afficher, sous des champs
 * corrects, une erreur portant sur la session choisie a l'etape 1.
 */
const schemaDeLEtape2 = schemaInscription.pick({
  prenomNom: true,
  email: true,
  telephone: true,
  ville: true,
  dispoQuand: true,
  dispoJour: true,
  remarque: true,
});

/**
 * Le meme schema, LA VILLE EN PLUS, pour le seul cas ou l'etape 2 affiche le champ.
 *
 * POURQUOI DEUX SCHEMAS ET NON UN. Depuis le 2026-08-20, le champ de la ville ne s'affiche que si
 * aucune session n'est retenue. `schemaInscription` a donc du rendre la ville facultative, sans quoi
 * une personne qui a choisi une date se serait vu refuser un champ qu'elle n'a jamais vu. Reste que
 * le champ, LORSQU'IL EST AFFICHE, est obligatoire : c'est ce que ce second schema tient.
 *
 * LA CONTRAINTE VIENT DE `schemaDeLaVille`, ELLE N'EST PAS RECRITE ICI : c'est celle de
 * `schemaDemande`, partagee avec la route `/api/reservation`.
 */
const schemaDeLEtape2AvecVille = schemaDeLEtape2.extend({ ville: schemaDeLaVille });

/**
 * Les reperes du fil, chacun avec le NUMERO D'ETAPE qu'il designe.
 *
 * LE NUMERO N'EST PAS LA POSITION, ET C'EST TOUT L'INTERET : quand aucune date n'est retenue, le
 * repere du paiement est retire, et « C'est enregistre » reste l'etape 5 dans l'etat du composant
 * tout en s'affichant en quatrieme position. Sans cette distinction, il aurait fallu renumeroter
 * l'etat selon le parcours, et deux jeux de numeros auraient coexiste dans le meme fichier.
 */
const REPERES_DU_FIL = parcours.fil.map((libelle, rang) => ({ libelle, etape: rang + 1 }));

/**
 * Le fil sans le paiement, pour qui n'a retenu aucune date.
 *
 * ⚠️ DEFAUT SIGNALE PAR WAHID LE 2026-08-20 : « on passe quand meme a la section paiement, alors
 * qu'il n'y a pas de paiement. Apres votre relecture, on devrait passer a C'est enregistre. » Une
 * personne sans date lisait « Votre virement reserve votre place » et un montant a virer, alors
 * qu'aucune session ne lui est encore proposee et qu'il n'y a donc rien a payer.
 *
 * `etape !== 4` PLUTOT QU'UN INDEX : le repere du paiement est le quatrieme de `contenu-site.md`,
 * et le retirer par sa position se casserait silencieusement le jour ou un repere s'insere avant.
 */
const REPERES_SANS_PAIEMENT = REPERES_DU_FIL.filter((repere) => repere.etape !== 4);

/**
 * Le choix de l'etape 1, en trois etats et non deux.
 *
 * `null` VEUT DIRE « RIEN N'EST ENCORE CHOISI », et la chaine vide veut dire « aucune de ces dates
 * ne me convient ». Les confondre rendrait le message « Choisissez une session, ou indiquez qu'aucune
 * date ne vous convient » inatteignable, alors que `contenu-site.md` v4 l'ecrit noir sur blanc.
 */
type ChoixDeSession = string | null;

/**
 * Le message a montrer pour un code d'erreur du schema. Les deux textes viennent de la spec.
 *
 * ⚠️ LE CODE `consentement` N'EST PLUS TRADUIT DEPUIS LE 2026-08-19, ET IL N'EST PLUS ATTEIGNABLE
 * D'ICI : la case a cocher a disparu de l'etape 2, `contenu-site.md` v16, et le schema pose le
 * consentement a vrai par defaut. Le message « Cochez cette case pour que je puisse vous ecrire »
 * a quitte le tableau des erreurs du document avec la case qu'il mesurait. La route, elle, refuse
 * toujours une requete qui posterait `consentement: false`, et elle ne rend aucun message.
 */
function messagePour(code: string): string {
  if (code === CODES.email) return parcours.erreurs.emailMalForme;
  return parcours.erreurs.champObligatoireVide;
}

/** Le texte des places restantes, dans les trois cas que la spec enumere. */
function placesDe(session: SessionDuParcours): string {
  if (session.placesRestantes <= 0) return etape1.places.complet;
  if (session.placesRestantes === 1) return etape1.places.une;
  return etape1.places.plusieurs.replace('{N}', String(session.placesRestantes));
}

export default function ParcoursDInscription({
  sessions = [],
}: {
  readonly sessions?: readonly SessionDuParcours[];
} = {}) {
  const [etape, setEtape] = useState(1);
  /*
   * QUAND AUCUNE SESSION N'EST OUVERTE, LE CHOIX EST DEJA FAIT, ET CE N'EST PAS UNE COMMODITE.
   *
   * Defaut mesure le 2026-08-15, en auditant l'etape 1 sur une base sans session ouverte : l'ecran
   * affichait « Aucune date n'est ouverte pour le moment. Laissez-moi vos coordonnees et vos
   * disponibilites », et le bouton Continuer repondait « Choisissez une session ». Il n'y avait rien
   * a choisir, et le parcours etait donc INFRANCHISSABLE exactement dans le cas ou la spec demande
   * qu'il recueille des coordonnees. Sans session proposee, l'etat de depart est celui de la
   * personne qu'aucune date n'arrange.
   */
  const [choix, setChoix] = useState<ChoixDeSession>(sessions.length === 0 ? '' : null);
  const [villeFiltree, setVilleFiltree] = useState('');
  const [erreurDEtape1, setErreurDEtape1] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);

  const {
    register,
    getValues,
    control,
    setError,
    setFocus,
    clearErrors,
    formState: { errors },
  } = useForm<DemandeDInscription>({ mode: 'onSubmit' });

  /*
   * LES VILLES PROPOSEES SONT CELLES DES SESSIONS AFFICHEES, ET RIEN D'AUTRE.
   *
   * `AC-INS-001` le prononce : « le filtre par ville n'offre que les villes portees par ces
   * sessions ». Une liste de villes ecrite a la main proposerait un filtre qui ne rend aucune ligne,
   * et la personne conclurait qu'il n'y a rien dans sa ville alors qu'aucune session n'y a jamais
   * ete ouverte.
   */
  const villes = useMemo(
    () => [...new Set(sessions.map((session) => session.ville))].sort((a, b) => a.localeCompare(b)),
    [sessions],
  );

  const sessionsAffichees = villeFiltree
    ? sessions.filter((session) => session.ville === villeFiltree)
    : sessions;

  const sessionChoisie = choix ? sessions.find((session) => session.id === choix) : undefined;

  /*
   * LA VILLE DE PREFERENCE N'EST DEMANDEE QU'A QUI N'A RETENU AUCUNE DATE, depuis le 2026-08-20.
   *
   * Demande de Wahid : « retirer le champ ville quand le choix de la session est fait et le
   * remplacer par "Ville de preference" quand pas de session disponible ». Quand une date est
   * retenue, la ville est celle de cette session, et la demander une seconde fois recueillerait une
   * valeur dont ni l'email de notification ni l'administration n'ont l'usage.
   *
   * `choix === ''` COUVRE LES DEUX CAS OU AUCUNE DATE N'EST RETENUE : la case « Aucune de ces dates
   * ne me convient », et la base sans aucune session ouverte, qui pose ce meme etat au depart.
   */
  const villeDemandee = choix === '';
  /*
   * LE PAIEMENT N'EST ATTENDU QUE SI UNE DATE EST RETENUE. Sans date, il n'y a ni place a reserver
   * ni montant a virer : l'envoi mene donc de l'etape 3 a l'etape 5, et le fil ne montre pas le
   * repere du paiement.
   *
   * `choix !== ''` ET NON `Boolean(sessionChoisie)` : au premier rendu, `choix` vaut `null`, « rien
   * n'est encore choisi ». Le fil montre alors ses cinq reperes, ce qui est la forme longue du
   * parcours ; il en perd un des que la personne coche « Aucune de ces dates ne me convient ».
   * `Boolean(sessionChoisie)` aurait fait demarrer le fil a quatre reperes pour tout le monde.
   */
  const paiementAttendu = choix !== '';
  const champsAffiches = CHAMPS.filter((champ) => champ.nom !== 'ville' || villeDemandee);

  function versEtape(cible: number) {
    setEchec(null);
    setEtape(cible);
  }

  /** L'etape 1 se quitte sur un choix explicite, y compris celui de n'avoir aucune date. */
  function quitteLEtape1() {
    if (choix === null) {
      setErreurDEtape1(parcours.erreurs.aucuneSessionChoisie);
      return;
    }
    setErreurDEtape1(null);
    versEtape(2);
  }

  /**
   * L'etape 2 se quitte quand les champs affiches passent le schema partage.
   *
   * LE SCHEMA CHOISI SUIT CE QUI EST A L'ECRAN : quand une session est retenue, le champ de la ville
   * n'est pas rendu, et exiger sa valeur afficherait une erreur sous un champ absent.
   */
  function quitteLEtape2() {
    const schema = villeDemandee ? schemaDeLEtape2AvecVille : schemaDeLEtape2;
    const lecture = schema.safeParse(getValues());
    if (lecture.success) {
      clearErrors();
      versEtape(3);
      return;
    }

    const enErreur: NomDeChamp[] = [];
    for (const probleme of lecture.error.issues) {
      const champ = probleme.path[0] as NomDeChamp | undefined;
      if (!champ) continue;
      setError(champ, { message: messagePour(probleme.message) });
      enErreur.push(champ);
    }
    // LE FOCUS PART SUR LE PREMIER CHAMP EN ERREUR. Sans lui, un lecteur au clavier apprend qu'il y
    // a une erreur et doit redescendre le formulaire a l'aveugle pour la trouver.
    if (enErreur.length > 0) setFocus(enErreur[0]);
  }

  /**
   * L'envoi de l'etape 3 : le schema complet, puis la route.
   *
   * LE CODE 409 RAMENE A L'ETAPE 1 AVEC SON MESSAGE, et c'est le seul endroit ou la route dit
   * quelque chose de plus qu'un statut : une session peut se remplir ou se fermer entre l'affichage
   * de l'etape 1 et l'envoi, et la personne doit pouvoir en choisir une autre plutot que de
   * recommencer.
   */
  async function envoie() {
    const lecture = schemaInscription.safeParse({
      ...getValues(),
      sessionId: choix ? choix : null,
      [CHAMP_PIEGE]: getValues(CHAMP_PIEGE) ?? '',
    });
    if (!lecture.success) {
      versEtape(2);
      return;
    }

    setEnvoi(true);
    try {
      const reponse = await fetch(`/api/inscription${window.location.search}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(lecture.data),
      });

      if (reponse.ok) {
        // LA LIGNE EST ECRITE ET LES DEUX EMAILS SONT PARTIS : l'etape 4 peut donc dire « Votre
        // demande est enregistree » sans que ce soit une promesse.
        //
        // LE TROISIEME DES TROIS EVENEMENTS DU SUIVI D'AUDIENCE PART ICI, ET NULLE PART AILLEURS :
        // il dit « envoi REUSSI », donc apres la reponse du serveur et jamais au clic. Il ne fait
        // rien quand le suivi n'est pas installe. RM-ANX-003, AC-ANX-013.
        envoiReussi('inscription');
        // L'ETAPE 4 EST SAUTEE QUAND AUCUNE DATE N'EST RETENUE, defaut signale par Wahid le
        // 2026-08-20. Voir `REPERES_SANS_PAIEMENT` pour le constat.
        setEtape(paiementAttendu ? 4 : 5);
        return;
      }

      if (reponse.status === 409) {
        const { motif } = (await reponse.json().catch(() => ({}))) as { motif?: string };
        /*
         * ⚠️ LE DOUBLON NE VIDE PAS LE CHOIX DE SESSION, ET LES DEUX AUTRES SI. Motif : la session
         * choisie est la BONNE dans ce cas, c'est la demande qui existe deja. La vider renverrait la
         * personne choisir une date alors que rien ne lui reste a choisir, et le message qui
         * s'affiche au-dessus dirait le contraire de ce que l'ecran fait.
         */
        if (motif === 'doublon') {
          setErreurDEtape1(parcours.erreurs.dejaInscrit);
          setEtape(1);
          return;
        }
        setChoix(null);
        setErreurDEtape1(
          motif === 'complete' ? parcours.erreurs.sessionComplete : parcours.erreurs.sessionFermee,
        );
        setEtape(1);
        return;
      }

      setEchec(parcours.echecTechnique);
    } catch {
      // UNE COUPURE RESEAU SE RACONTE COMME UN ECHEC TECHNIQUE : c'est le meme message, et il porte
      // l'adresse email de repli. Le lecteur n'a pas a distinguer les deux causes.
      setEchec(parcours.echecTechnique);
    } finally {
      setEnvoi(false);
    }
  }

  /*
   * LE RECAPITULATIF DE L'ETAPE 3 RELIT LES CHAMPS SAISIS AUX ETAPES 1 ET 2.
   *
   * `useWatch` plutot que le `watch()` rendu par `useForm` : les deux abonnent ce composant a tous
   * les champs et rendent le meme objet, mais `watch()` rend une fonction que le React Compiler ne
   * sait pas memoriser, et il renonce alors a optimiser tout le composant. C'est ce que rapporte
   * `react-hooks/incompatible-library`, seul avertissement restant de `npm run lint`.
   */
  const valeurs = useWatch({ control }) as Partial<DemandeDInscription>;

  return (
    <div data-parcours="inscription" data-etape={etape} className="flex flex-col gap-10">
      <FilDesEtapes
        reperes={paiementAttendu ? REPERES_DU_FIL : REPERES_SANS_PAIEMENT}
        courante={etape}
      />

      {etape === 1 ? (
        <section data-ecran="session" aria-labelledby="etape-1-titre" className="flex flex-col gap-6">
          <h2 id="etape-1-titre" className="font-titre text-titre-section text-texte-fort">
            {etape1.titre}
          </h2>
          <p className="font-corps text-corps text-texte-doux">{etape1.aide}</p>

          {sessions.length === 0 ? (
            <p data-session="aucune-ouverte" className="font-corps text-corps text-texte-courant">
              {etape1.aucuneOuverte}
            </p>
          ) : (
            <>
              {/*
                LE FILTRE NE S'AFFICHE QU'A PARTIR DE DEUX VILLES, depuis le 2026-08-20, demande de
                Wahid. Sur une seule ville, il n'offrait que « Toutes les villes » et cette ville :
                les deux entrees rendent exactement les memes sessions, et le filtre demandait donc
                une decision qui ne change rien a l'ecran.

                `villeFiltree` RESTE A LA CHAINE VIDE QUAND LE FILTRE EST MASQUE, et c'est ce qui
                fait que `sessionsAffichees` les rend toutes. Rien d'autre n'ecrit cet etat.
              */}
              {villes.length > 1 ? (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="filtre-ville"
                  className="font-corps font-medium text-corps text-texte-courant"
                >
                  {etape1.filtre.intitule}
                </label>
                <select
                  id="filtre-ville"
                  data-filtre="ville"
                  value={villeFiltree}
                  onChange={(evenement) => setVilleFiltree(evenement.target.value)}
                  className={cn(
                    'h-13 w-full rounded-champ border border-bordure bg-fond-eleve px-4',
                    'font-corps text-corps text-texte-courant',
                    'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
                  )}
                >
                  <option value="">{etape1.filtre.toutesLesVilles}</option>
                  {villes.map((ville) => (
                    <option key={ville} value={ville}>
                      {ville}
                    </option>
                  ))}
                </select>
              </div>
              ) : null}

              <fieldset className="flex flex-col gap-3 border-0 p-0">
                <legend className="sr-only">{etape1.titre}</legend>

                {sessionsAffichees.map((session) => (
                  <label
                    key={session.id}
                    htmlFor={`session-${session.id}`}
                    data-session={session.id}
                    data-choisie={choix === session.id ? 'oui' : undefined}
                    className={cn(CARTE_DE_CHOIX, choix === session.id && CARTE_DE_CHOIX_ACTIVE)}
                  >
                    <input
                      id={`session-${session.id}`}
                      type="radio"
                      name="choix-de-session"
                      value={session.id}
                      checked={choix === session.id}
                      onChange={() => {
                        setChoix(session.id);
                        setErreurDEtape1(null);
                      }}
                      className="mt-1 h-5 w-5 shrink-0 accent-action"
                    />
                    {/*
                      L'ORDRE DES ELEMENTS EST CELUI DE LA SPEC : la date, la ville, l'horaire, le
                      lieu, et le nombre de places restantes. LES TROIS PREMIERS SONT SUR TROIS
                      LIGNES, chacun precede de son intitule, `contenu-site.md` v16 § Etape 1.

                      DEUX CHANGEMENTS DU 2026-08-20, demandes par Wahid. La ville n'a plus sa ligne :
                      elle se lit au bout de celle du lieu, ce que `LignesDeSession` construit. Et le
                      nombre de places quitte la colonne pour se poser en haut a droite de la carte,
                      en pastille. Il n'a toujours pas d'intitule : il se lit seul, et le document le
                      dit.
                    */}
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <LignesDeSession session={session} />
                    </span>
                    <span data-session-places className={PASTILLE_DE_PLACES}>
                      {placesDe(session)}
                    </span>
                  </label>
                ))}

                <label
                  htmlFor="session-aucune"
                  data-session="aucune"
                  data-choisie={choix === '' ? 'oui' : undefined}
                  className={cn(CARTE_DE_CHOIX, choix === '' && CARTE_DE_CHOIX_ACTIVE)}
                >
                  <input
                    id="session-aucune"
                    type="radio"
                    name="choix-de-session"
                    value=""
                    checked={choix === ''}
                    onChange={() => {
                      setChoix('');
                      setErreurDEtape1(null);
                    }}
                    className="mt-1 h-5 w-5 shrink-0 accent-action"
                  />
                  <span className="flex flex-col gap-1">
                    <span>{etape1.sansDate.libelle}</span>
                    <span className="text-legende text-texte-doux">{etape1.sansDate.aide}</span>
                  </span>
                </label>
              </fieldset>
            </>
          )}

          <div
            data-erreur="etape-1"
            aria-live="assertive"
            role="alert"
            className="font-corps text-legende text-action-survol"
          >
            {erreurDEtape1}
          </div>

          {/*
            LE BOUTON EST UN `button` ORDINAIRE ET NON LE COMPOSANT `Bouton` QUAND IL NE SOUMET RIEN.
            `Bouton` rend un `type="submit"` des qu'on ne lui donne pas de cible, et un `submit` hors
            formulaire ne fait rien. Ici, passer a l'etape suivante est une action, pas un envoi.
          */}
          <BoutonDEtape repere="etape-1-continuer" libelle={etape1.bouton} onClick={quitteLEtape1} />
        </section>
      ) : null}

      {etape === 2 ? (
        <section
          data-ecran="coordonnees"
          aria-labelledby="etape-2-titre"
          className="flex flex-col gap-6"
        >
          <h2 id="etape-2-titre" className="font-titre text-titre-section text-texte-fort">
            {etape2.titre}
          </h2>
          {/*
            L'ETAPE 2 N'A PLUS D'AIDE SOUS SON TITRE DEPUIS LE 2026-08-20, `contenu-site.md` v18.
            Wahid a demande d'enlever « Elles me servent a vous ecrire quand la session se confirme.
            Rien d'autre. » La cle `aide` a quitte `contenu.inscription.etapes.vosCoordonnees` avec
            la phrase : la garder vide aurait laisse un paragraphe vide dans le document rendu, et
            `AC-INS-009` compare chaque chaine de cette cle au document.
          */}

          {/*
            DEUX COLONNES SUR ORDINATEUR, UNE SEULE SUR TELEPHONE. Demande de Wahid du 2026-08-20 :
            « sur desktop mettre Email et Tel du formulaire en 2 colonnes ». Seuls ces deux champs
            partagent une ligne ; les autres reprennent les deux colonnes, ce que `md:col-span-2`
            ecrit. Sous 768 pixels la grille n'a qu'une colonne et la portee est sans effet.
          */}
          <div className="grid gap-6 md:grid-cols-2">
            {champsAffiches.map((champ) => (
              <div key={champ.nom} className={champ.demiLargeur ? undefined : 'md:col-span-2'}>
                <Champ
                  identifiant={champ.nom}
                  libelle={etape2.champs[champ.rang].libelle}
                  type={champ.type}
                  obligatoire={etape2.champs[champ.rang].obligatoire}
                  erreur={errors[champ.nom]?.message}
                  enregistrement={{ ...register(champ.nom), autoComplete: champ.autoComplete }}
                  choix={champ.nom === 'ville' ? VILLES_DU_MAROC : undefined}
                  entreeVide={champ.nom === 'ville' ? etape2.villeEntreeVide : undefined}
                />
              </div>
            ))}
          </div>

          {/*
            LES DISPONIBILITES NE S'AFFICHENT QUE SI AUCUNE SESSION N'A ETE CHOISIE, et c'est la regle
            de `contenu-site.md` v4 : « quand une date est choisie, la disponibilite est cette date, et
            redemander une preference n'aurait aucun effet ».
          */}
          {choix === '' ? (
            <fieldset className="flex flex-col gap-3 border-0 p-0">
              <legend className="font-corps font-medium text-corps text-texte-courant">
                {etape2.disponibilites.intitule}
              </legend>
              <p className="font-corps text-legende text-texte-doux">{etape2.disponibilites.aide}</p>

              <div className="mt-2 flex flex-col gap-5 md:flex-row md:gap-10">
                {(['quand', 'quelJour'] as const).map((groupe) => (
                  <fieldset key={groupe} className="flex flex-col gap-2 border-0 p-0">
                    <legend className="font-structure text-etiquette uppercase tracking-etiquette text-texte-doux">
                      {etape2.disponibilites[groupe].intitule}
                    </legend>
                    {etape2.disponibilites[groupe].options.map((option, rang) => (
                      <label
                        key={option}
                        htmlFor={`${groupe}-${rang}`}
                        className="flex items-start gap-3 font-corps text-corps text-texte-courant"
                      >
                        <input
                          id={`${groupe}-${rang}`}
                          type="checkbox"
                          value={option}
                          className="mt-1 h-5 w-5 shrink-0 accent-action"
                          {...register(groupe === 'quand' ? 'dispoQuand' : 'dispoJour')}
                        />
                        {option}
                      </label>
                    ))}
                  </fieldset>
                ))}
              </div>
            </fieldset>
          ) : null}

          <div className="flex flex-col gap-2">
            <label
              htmlFor="remarque"
              className="font-corps font-medium text-corps text-texte-courant"
            >
              {etape2.remarque.intitule}
            </label>
            <p id="remarque-aide" className="font-corps text-legende text-texte-doux">
              {etape2.remarque.aide}
            </p>
            <textarea
              id="remarque"
              rows={4}
              aria-describedby="remarque-aide"
              {...register('remarque')}
              className={cn(
                'w-full rounded-champ border border-bordure bg-fond-eleve px-4 py-3',
                'font-corps text-corps text-texte-courant',
                'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
              )}
            />
          </div>

          {/*
            LA MENTION DE CONSENTEMENT A REMPLACE LA CASE A COCHER LE 2026-08-19, `contenu-site.md`
            v16 § Etape 2. Ce n'est plus un champ : elle ne se coche pas, elle ne peut pas etre en
            erreur, et le schema pose le consentement a vrai quand la demande part. Elle reste ICI,
            sous les champs et au-dessus du bouton, parce que la phrase dit « en continuant » : la
            lire apres le bouton la rendrait fausse pour qui a deja continue.
          */}
          <p data-mention="consentement" className="font-corps text-legende text-texte-doux">
            {etape2.mentionDeConsentement}
          </p>

          {/*
            LE CHAMP PIEGE. Il ressemble a un champ ordinaire pour un robot qui remplit tout ce qu'il
            trouve, et il n'existe ni pour l'oeil, ni pour le clavier, ni pour un lecteur d'ecran.
          */}
          <input
            type="text"
            hidden
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
            {...register(CHAMP_PIEGE)}
          />

          <RegionDErreurs messages={messagesDe(errors)} echec={echec} />

          <div className="flex flex-col gap-4">
            <BoutonDEtape
              repere="etape-2-continuer"
              libelle={etape2.bouton}
              onClick={quitteLEtape2}
            />
            <LienDEtape
              repere="etape-2-retour"
              libelle={etape2.lienRetour}
              onClick={() => versEtape(1)}
            />
          </div>
        </section>
      ) : null}

      {etape === 3 ? (
        <section
          data-ecran="relecture"
          aria-labelledby="etape-3-titre"
          className="flex flex-col gap-6"
        >
          <h2 id="etape-3-titre" className="font-titre text-titre-section text-texte-fort">
            {etape3.titre}
          </h2>
          <p className="font-corps text-corps text-texte-doux">{etape3.aide}</p>

          {/*
            CHAQUE BLOC RAMENE A L'ETAPE QUI L'A SAISI, ET LES AUTRES NE SONT PAS PERDUES : c'est
            `AC-INS-003`, et c'est aussi le pas `RP-02-4`. Le retour ne fait que changer le numero
            d'etape ; ni les valeurs du formulaire, ni le choix de session ne sont remis a zero.
          */}
          <section data-bloc="session" className="flex flex-col gap-2">
            <h3 className="font-corps font-medium text-corps text-texte-courant">
              {etape3.blocs[0].intitule}
            </h3>
            {/*
              LE BLOC DE LA SESSION REPREND LES QUATRE INTITULES DE L'ETAPE 1, et c'est le mot de
              `contenu-site.md` v16 § Etape 3. Les quatre valeurs tenaient sur une ligne, separees
              par des points medians, jusqu'au 2026-08-19.
            */}
            {sessionChoisie ? (
              <p
                data-recapitulatif="session"
                className="flex flex-col gap-1 font-corps text-corps text-texte-courant"
              >
                <LignesDeSession session={sessionChoisie} />
              </p>
            ) : (
              <p className="font-corps text-corps text-texte-courant">{etape3.mentionSansDate}</p>
            )}
            <LienDEtape
              repere="corriger-session"
              libelle={etape3.blocs[0].lien}
              onClick={() => versEtape(1)}
            />
          </section>

          <section data-bloc="coordonnees" className="flex flex-col gap-2">
            <h3 className="font-corps font-medium text-corps text-texte-courant">
              {etape3.blocs[1].intitule}
            </h3>
            <dl className="flex flex-col gap-1 font-corps text-corps text-texte-courant">
              {champsAffiches.map((champ) => (
                <div key={champ.nom} className="flex flex-wrap gap-x-2">
                  <dt className="text-texte-doux">{etape2.champs[champ.rang].libelle}</dt>
                  <dd data-recapitulatif={champ.nom}>{String(valeurs[champ.nom] ?? '')}</dd>
                </div>
              ))}
              {/*
                LES DISPONIBILITES ET LA REMARQUE NE SE RECAPITULENT QUE SI ELLES ONT ETE RENSEIGNEES,
                et c'est le mot de la spec : « les disponibilites SI elles ont ete cochees, la
                remarque SI elle a ete ecrite ». Un intitule suivi d'un vide ferait croire a une perte.
              */}
              {disponibilitesCochees(valeurs).length > 0 ? (
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-texte-doux">{etape2.disponibilites.intitule}</dt>
                  <dd data-recapitulatif="disponibilites">
                    {disponibilitesCochees(valeurs).join(', ')}
                  </dd>
                </div>
              ) : null}
              {valeurs.remarque ? (
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-texte-doux">{etape2.remarque.intitule}</dt>
                  <dd data-recapitulatif="remarque">{valeurs.remarque}</dd>
                </div>
              ) : null}
            </dl>
            <LienDEtape
              repere="corriger-coordonnees"
              libelle={etape3.blocs[1].lien}
              onClick={() => versEtape(2)}
            />
          </section>

          <RegionDErreurs messages={[]} echec={echec} />

          <div className="flex flex-col gap-4">
            <BoutonDEtape
              repere="etape-3-envoyer"
              libelle={envoi ? parcours.erreurs.boutonEnvoiEnCours : etape3.bouton}
              onClick={envoie}
            />
            <LienDEtape
              repere="etape-3-retour"
              libelle={etape3.lienRetour}
              onClick={() => versEtape(2)}
            />
          </div>
        </section>
      ) : null}

      {etape === 4 ? (
        <section
          data-ecran="virement"
          aria-labelledby="etape-4-titre"
          className="flex flex-col gap-6"
        >
          <h2 id="etape-4-titre" className="font-titre text-titre-section text-texte-fort">
            {etape4.titre}
          </h2>
          {etape4.corps.map((paragraphe) => (
            <p key={paragraphe} className="font-corps text-corps text-texte-courant">
              {paragraphe}
            </p>
          ))}

          {/*
            L'ANNONCE DE L'EMAIL DE PAIEMENT EST LE DEUXIEME PARAGRAPHE DU CORPS DEPUIS LE 2026-08-19,
            et non plus une chaine a part. Elle a remplace l'annonce d'un appel telephonique, posee le
            2026-08-16, elle-meme venue a la place de la liste des quatre coordonnees bancaires.
            L'ecran ne porte toujours ni valeur bancaire, ni mention `[a completer : ...]` :
            `AC-INS-004` en compte zero.
          */}
          <p className="font-corps text-corps text-texte-doux">{etape4.ceQuiSuit}</p>

          <BoutonDEtape
            repere="etape-4-terminer"
            libelle={etape4.bouton}
            onClick={() => versEtape(5)}
          />
        </section>
      ) : null}

      {etape === 5 ? (
        <section
          data-ecran="confirmation"
          aria-labelledby="etape-5-titre"
          role="status"
          className="flex flex-col gap-6"
        >
          <h2 id="etape-5-titre" className="font-titre text-titre-section text-texte-fort">
            {etape5.titre}
          </h2>

          {/*
            DEUX CORPS, ET LA SPEC LES ECRIT TOUS LES DEUX : celui de qui a retenu une session, ou
            `{date}` et `{ville}` se remplacent, et celui de qui n'en a retenu aucune. Ecrire un
            texte unique qui couvrirait les deux cas aurait demande une phrase que le document n'a pas.
          */}
          {(sessionChoisie ? etape5.corpsAvecSession : etape5.corpsSansSession).map((paragraphe) => (
            <p key={paragraphe} className="font-corps text-corps text-texte-courant">
              {sessionChoisie
                ? paragraphe
                    .replace('{date}', sessionChoisie.jour)
                    .replace('{ville}', sessionChoisie.ville)
                : paragraphe}
            </p>
          ))}

          <p className="font-corps text-corps text-texte-doux">{etape5.sousLeCorps}</p>

          {/*
            `Link` ET NON UN `a` NU : la page d'accueil est une page de ce site, et Next refuse une
            navigation interne par `a`. Le rendu reste un `a` portant `href="/"`.
          */}
          <Link
            href="/"
            data-lien="retour-journee"
            className={cn(
              'self-start font-corps text-corps text-texte-accent underline underline-offset-4',
              'min-h-11 hover:text-action-survol',
              'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
            )}
          >
            {etape5.lien}
          </Link>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Les trois lignes d'une session : Date, Horaire, Lieu, chacune precedee de son intitule.
 *
 * IL EST ECRIT UNE FOIS ET RENDU A DEUX ENDROITS, l'etape 1 et le bloc de session du recapitulatif.
 * `contenu-site.md` v16 § Etape 3 ecrit « Le bloc de la session reprend les quatre intitules du
 * § Etape 1 » : deux rendus separes auraient permis a l'un des deux de deriver sans que rien ne le
 * signale, ce qui est exactement ce qui s'etait produit avec la mise en forme de la date, faite sur
 * la page et absente des emails.
 *
 * ⚠️ LA VILLE N'A PLUS SA PROPRE LIGNE DEPUIS LE 2026-08-20, ET ELLES SONT DONC TROIS ET NON QUATRE.
 * Demande de Wahid, mot pour mot : « le lieu devrait voir : "[Lieu], [Ville]" et retirer la ligne
 * "Ville :" ». Les deux valeurs designent le meme endroit, et les lire sur deux lignes obligeait a
 * les rapprocher de tete. `intitules.ville` de `contenu.inscription` n'est donc plus rendu par ce
 * composant ; il reste dans le contenu, ou `contenu-site.md` v16 § Etape 1 l'ecrit.
 *
 * LES INTITULES VIENNENT DE `contenu.inscription`, pas de ce fichier. Le double point qui les suit
 * est la ponctuation du document, qui ecrit « Date : {date} » dans l'email de confirmation. LA
 * VIRGULE ENTRE LE LIEU ET LA VILLE est celle de la demande de Wahid, recopiee de sa tournure.
 */
function LignesDeSession({ session }: { readonly session: SessionDuParcours }) {
  const lignes = [
    { cle: 'date', intitule: intitulesDeSession.date, valeur: session.jour },
    {
      cle: 'horaire',
      intitule: intitulesDeSession.horaire,
      valeur: enHoraireLisible(gabaritHoraire, session.debut, session.fin),
    },
    {
      cle: 'lieu',
      intitule: intitulesDeSession.lieu,
      // LA VILLE NE S'AJOUTE QUE SI ELLE EST RENSEIGNEE : la colonne accepte la chaine vide, et
      // « Hyatt Regency, » se lirait comme une valeur tronquee.
      valeur: [session.lieu, session.ville].filter(Boolean).join(', '),
    },
  ];

  return (
    <>
      {lignes.map((ligne) => (
        <span key={ligne.cle} data-session-ligne={ligne.cle}>
          <span className="text-texte-doux">{ligne.intitule} : </span>
          {ligne.valeur}
        </span>
      ))}
    </>
  );
}

/**
 * Les reperes du haut, avec celui de l'etape en cours marque pour les lecteurs d'ecran.
 *
 * TROIS ETATS, ET NON DEUX, depuis le 2026-08-15 : franchie, courante, a venir. Le fil ne disait
 * que « celle-ci est la courante », en gras. Il dit maintenant OU L'ON EN EST : combien d'etapes
 * sont derriere, combien restent. Demande de Wahid, faite apres le run du sprint 07.
 *
 * LES NUMEROS NE SONT PAS UN TEXTE INVENTE, et `CLAUDE.md` §8 est respecte : `contenu-site.md`
 * numerote ses cinq sections « Etape 1. La session », « Etape 2. Vos coordonnees », et ainsi de
 * suite. Le chiffre affiche est celui du document. Il est en outre `aria-hidden` : il double le
 * libelle pour l'oeil, il ne l'annonce pas deux fois au lecteur d'ecran.
 *
 * SUR TELEPHONE, LES LIBELLES SE MASQUENT ET LES PASTILLES RESTENT. Cinq libelles cote a cote ne
 * tiennent pas sur 390 pixels, et les faire passer a la ligne rendait un pave de cinq lignes. Le
 * libelle de l'etape courante est ecrit SOUS le fil, une seule fois. Les `data-fil-etape` restent
 * dans le document dans les deux cas, ce que `PU-02` compte.
 *
 * ⚠️ IL RECOIT SES REPERES, IL NE LES LIT PLUS DANS `contenu.inscription`, depuis le 2026-08-20.
 * Motif : le parcours sans date n'en a que quatre, le repere du paiement etant retire. Voir
 * `REPERES_SANS_PAIEMENT`.
 *
 * `courante` EST UN NUMERO D'ETAPE, PAS UNE POSITION, et `position` fait la conversion. Comparer
 * `courante` au rang aurait allume le mauvais repere des qu'un repere manque : a l'etape 5 du
 * parcours sans date, qui s'affiche en quatrieme position, aucun repere n'aurait ete courant.
 *
 * AUCUNE ICONE, AUCUNE DEPENDANCE : l'etape franchie se marque par le remplissage de sa pastille,
 * pas par une coche. Le trait de liaison est une bordure, pas un SVG.
 */
function FilDesEtapes({
  reperes,
  courante,
}: {
  readonly reperes: readonly { readonly libelle: string; readonly etape: number }[];
  readonly courante: number;
}) {
  const position = reperes.findIndex((repere) => repere.etape === courante);

  return (
    <nav data-fil="etapes" aria-label={parcours.titre}>
      <ol className="flex items-center">
        {reperes.map(({ libelle: repere }, rang) => {
          const rangUn = rang + 1;
          const franchie = rang < position;
          const estCourante = rang === position;

          return (
            <li
              key={repere}
              data-fil-etape={rangUn}
              aria-current={estCourante ? 'step' : undefined}
              // LE REPERE NE S'ETIRE PAS, SEUL LE TRAIT S'ETIRE. Mesure du 2026-08-15 : avec
              // `flex-1` pose ici, les cinq reperes se partageaient la largeur a parts egales et
              // les libelles sortaient tronques, « La sessi... », « Vos coo... ».
              className={cn(
                'flex shrink-0 items-center gap-2',
                rang < reperes.length - 1 && 'grow',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full',
                  'font-structure text-etiquette tabular-nums',
                  franchie && 'bg-action text-texte-inverse',
                  estCourante && 'border-2 border-action bg-fond-eleve font-semibold text-texte-accent',
                  !franchie && !estCourante && 'border border-bordure-forte bg-fond-eleve text-texte-doux',
                )}
              >
                {rangUn}
              </span>

              <span
                className={cn(
                  'hidden whitespace-nowrap font-corps text-etiquette md:inline',
                  estCourante ? 'font-medium text-texte-fort' : 'text-texte-doux',
                )}
              >
                {repere}
              </span>

              {rang < reperes.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-px min-w-4 flex-1',
                    franchie ? 'bg-action' : 'bg-bordure-forte',
                  )}
                />
              ) : null}
            </li>
          );
        })}
      </ol>

      {/*
        LE LIBELLE DE L'ETAPE COURANTE, SUR TELEPHONE SEULEMENT, ou les libelles du fil sont
        masques. Il est `aria-hidden` : le meme libelle est deja dans le fil, porte par le repere
        `aria-current`, et l'annoncer deux fois ferait entendre la meme etape en double.
      */}
      <p
        aria-hidden="true"
        className="mt-2 font-corps text-legende font-medium text-texte-fort md:hidden"
      >
        {reperes[position]?.libelle}
      </p>
    </nav>
  );
}

/**
 * La region d'annonce, TOUJOURS DANS LE DOCUMENT, MEME VIDE.
 *
 * C'est ce qui la fait fonctionner : une region `aria-live` inseree en meme temps que son texte
 * n'est pas annoncee par tous les lecteurs d'ecran. Elle est posee vide au rendu, et seul son
 * contenu change.
 */
function RegionDErreurs({
  messages,
  echec,
}: {
  readonly messages: readonly string[];
  readonly echec: string | null;
}) {
  return (
    <div
      data-parcours-erreurs
      aria-live="assertive"
      role="alert"
      className="flex flex-col gap-1 font-corps text-legende text-action-survol"
    >
      {echec}
      {/*
        LES ERREURS DE SAISIE S'ANNONCENT ICI ET S'AFFICHENT SOUS LEUR CHAMP, et les deux formes ne se
        doublent pas a l'ecran : un message sous le champ dit OU corriger, un resume dit QU'IL Y A a
        corriger. La liste est donc reservee aux lecteurs d'ecran.
      */}
      {messages.length > 0 ? (
        <span className="sr-only">
          {messages.map((message, rang) => (
            <span key={rang}>{message} </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

/** Un bouton d'etape : il agit, il n'envoie pas de formulaire. */
function BoutonDEtape({
  repere,
  libelle,
  onClick,
}: {
  readonly repere: string;
  readonly libelle: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-bouton={repere}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center text-center',
        'font-corps font-semibold text-bouton',
        'w-full rounded-carte bg-action px-8 py-4 text-texte-inverse md:w-auto',
        'transition-colors duration-150 ease-douce hover:bg-action-survol',
        'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
      )}
    >
      {libelle}
    </button>
  );
}

/** Un retour ou une correction : une action, rendue comme un lien de texte. */
function LienDEtape({
  repere,
  libelle,
  onClick,
}: {
  readonly repere: string;
  readonly libelle: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-lien={repere}
      onClick={onClick}
      className={cn(
        'self-start font-corps text-corps text-texte-accent underline underline-offset-4',
        'min-h-11 hover:text-action-survol',
        'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
      )}
    >
      {libelle}
    </button>
  );
}

/** Les messages d'erreur poses sur les champs, dans l'ordre ou `react-hook-form` les rend. */
function messagesDe(errors: Record<string, { message?: string } | undefined>): string[] {
  return Object.keys(errors)
    .map((champ) => errors[champ]?.message)
    .filter((message): message is string => Boolean(message));
}

/** Les cases de disponibilite cochees, les deux groupes confondus. */
function disponibilitesCochees(valeurs: Partial<DemandeDInscription>): string[] {
  const quand = Array.isArray(valeurs.dispoQuand) ? valeurs.dispoQuand : [];
  const jour = Array.isArray(valeurs.dispoJour) ? valeurs.dispoJour : [];
  return [...quand, ...jour];
}
